import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { 
  Database, 
  Settings, 
  Upload, 
  Plus, 
  Download, 
  RefreshCcw, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  FileArchive,
  ArrowUpCircle,
  ShieldCheck
} from 'lucide-react';

interface BackupFile {
  fileName: string;
  size: number;
  createdAt: string;
  status: 'success' | 'failed' | 'in_progress';
  type: 'full' | 'database' | 'uploads';
}

interface BackupConfig {
  enabled: boolean;
  schedule: string;
  retentionDays: number;
  storageType: 'local' | 's3';
}

const SCHEDULE_OPTIONS = [
  { value: '0 */6 * * *', label: 'A cada 6 horas' },
  { value: '0 */12 * * *', label: 'A cada 12 horas' },
  { value: '0 2 * * *', label: 'Diariamente às 2h' },
  { value: '0 2 * * 0', label: 'Semanalmente (Domingo às 2h)' },
  { value: '0 2 1 * *', label: 'Mensalmente (Dia 1 às 2h)' }
];

export function SystemBackup() {
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [config, setConfig] = useState<BackupConfig>({
    enabled: false,
    schedule: '0 2 * * *',
    retentionDays: 30,
    storageType: 'local'
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    });
  }, []);

  const loadBackups = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system');
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
      } else {
        throw new Error('Erro ao carregar backups');
      }
    } catch (error) {
      console.error('Erro ao carregar backups:', error);
      toast.error('Erro ao carregar lista de backups');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const loadConfig = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system/config');
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setConfig(data.config);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadBackups();
    loadConfig();
  }, [loadBackups, loadConfig]);

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await authenticatedFetch('/api/backup/system', {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Backup criado com sucesso!');
        await loadBackups();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao criar backup');
      }
    } catch (error) {
      console.error('Erro ao criar backup:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const response = await authenticatedFetch('/api/backup/system/configure', {
        method: 'POST',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuração salva com sucesso!');
        setShowConfigModal(false);
        await loadConfig();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao salvar configuração');
      }
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar configuração');
    }
  };

  const handleDownloadBackup = async (fileName: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/backup/system/download/${fileName}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao fazer download do backup');
      }

      // Criar blob e fazer download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Download iniciado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer download do backup');
    }
  };

  const handleUploadAndRestore = async () => {
    if (!uploadFile) {
      toast.error('Selecione um arquivo de backup');
      return;
    }

    // Validar extensão do arquivo
    if (!uploadFile.name.endsWith('.tar.gz')) {
      toast.error('Arquivo deve ser um backup válido (.tar.gz)');
      return;
    }

    showConfirm(
      'Atenção Crítica',
      '⚠️ ATENÇÃO CRÍTICA: Esta ação irá:\n\n' +
      '1. Fazer upload do arquivo de backup\n' +
      '2. SUBSTITUIR COMPLETAMENTE o banco de dados atual\n' +
      '3. SUBSTITUIR COMPLETAMENTE todos os arquivos\n' +
      '4. EXCLUIR todos os dados atuais\n\n' +
      'Esta ação é IRREVERSÍVEL e NÃO PODE SER DESFEITA!\n\n' +
      'Tem CERTEZA ABSOLUTA que deseja continuar?',
      async () => {
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('backup', uploadFile);

          const token = localStorage.getItem('auth_token');
          const response = await fetch('/api/backup/system/upload', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`
            },
            body: formData
          });

          if (response.ok) {
            const data = await response.json();
            toast.success('Backup enviado com sucesso! Aguarde a restauração...');

            // Agora restaurar o backup que acabou de fazer upload
            const restoreResponse = await authenticatedFetch('/api/backup/system/restore', {
              method: 'POST',
              body: JSON.stringify({ fileName: data.fileName })
            });

            if (restoreResponse.ok) {
              toast.success('Sistema restaurado com sucesso! Recomenda-se reiniciar os serviços.');
              setShowUploadModal(false);
              setUploadFile(null);
              await loadBackups();
            } else {
              const error = await restoreResponse.json();
              throw new Error(error.message || 'Erro ao restaurar backup');
            }
          } else {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Erro ao enviar arquivo de backup');
          }
        } catch (error) {
          console.error('Erro ao fazer upload e restaurar:', error);
          toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload e restaurar backup');
        } finally {
          setUploading(false);
        }
      },
      'danger'
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
        <div className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando backups...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header e Ações */}
      <div className="bg-white/5 p-8 rounded-[2.5rem] border border-white/10 backdrop-blur-md relative overflow-hidden group/header shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent opacity-0 group-hover/header:opacity-100 transition-opacity duration-500" />
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
          <div>
            <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">Segurança de Dados</h2>
            <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase tracking-[0.2em] font-oswald">
              Gerenciamento completo de backups e restauração do sistema
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setShowConfigModal(true)}
              className="px-6 py-3 text-[10px] font-bold font-oswald text-gray-400 uppercase tracking-[0.2em] bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:text-white transition-all flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Configurar
            </button>
            
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 text-[10px] font-bold font-oswald text-white uppercase tracking-[0.2em] bg-orange-600/20 border border-orange-500/30 rounded-2xl hover:bg-orange-600/30 transition-all flex items-center gap-2 shadow-lg shadow-orange-900/20"
            >
              <ArrowUpCircle className="w-4 h-4" />
              Restaurar de Arquivo
            </button>
            
            <button
              onClick={handleCreateBackup}
              disabled={creating}
              className="px-8 py-3.5 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] hover:bg-blue-500 shadow-xl shadow-blue-900/40 hover:-translate-y-1 disabled:opacity-50 disabled:translate-y-0 transition-all flex items-center gap-3 group/btn overflow-hidden border border-blue-400/50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              {creating ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {creating ? 'Criando...' : 'Criar Backup'}
            </button>
          </div>
        </div>

        {/* Status do Backup Automático */}
        <div className="mt-10 p-6 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm relative group/status">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent opacity-0 group-hover/status:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-5">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border transition-all ${
                config.enabled ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-gray-500/10 border-gray-500/20 text-gray-500'
              }`}>
                {config.enabled ? <ShieldCheck className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
              </div>
              <div>
                <p className="text-xs font-bold text-white uppercase tracking-widest font-oswald">
                  Backup Automático: {config.enabled ? <span className="text-green-400">ATIVADO</span> : <span className="text-gray-500">DESATIVADO</span>}
                </p>
                {config.enabled && (
                  <p className="text-[10px] text-gray-400 mt-1 font-bold uppercase tracking-[0.1em] font-oswald">
                    Agendamento: {SCHEDULE_OPTIONS.find(opt => opt.value === config.schedule)?.label || config.schedule}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowConfigModal(true)}
              className="px-5 py-2 text-[9px] font-bold text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded-xl border border-blue-400/20 transition-all font-oswald uppercase tracking-widest"
            >
              {config.enabled ? 'Modificar' : 'Ativar Agora'}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Backups */}
      <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden relative group/list">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-[80px] -mr-32 -mt-32 opacity-50" />
        
        <div className="px-10 py-8 border-b border-white/10 bg-white/5 flex justify-between items-center relative z-10">
          <div>
            <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight">Backups Disponíveis</h3>
            <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-[0.2em] font-oswald">
              {backups.length} arquivo(s) de segurança encontrado(s)
            </p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20 text-blue-400">
            <Database className="w-6 h-6" />
          </div>
        </div>

        {backups.length === 0 ? (
          <div className="p-24 text-center relative z-10">
            <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-white/10 shadow-inner">
              <FileArchive className="w-10 h-10 text-gray-600" />
            </div>
            <p className="text-lg font-bold text-white font-oswald uppercase tracking-tight">Nenhum backup encontrado</p>
            <p className="text-[10px] font-bold text-gray-500 mt-2 uppercase tracking-[0.2em] font-oswald">Clique em "Criar Backup" para iniciar sua primeira cópia de segurança</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5 relative z-10">
            {backups.map((backup) => (
              <div key={backup.fileName} className="px-10 py-6 hover:bg-white/5 transition-all group/item">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-center gap-6">
                    <div className="w-12 h-12 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center group-hover/item:border-blue-500/30 group-hover/item:bg-blue-500/5 transition-all">
                      <FileArchive className="w-6 h-6 text-gray-400 group-hover/item:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white font-oswald uppercase tracking-wide">{backup.fileName}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald bg-white/5 px-2 py-1 rounded-md">{formatFileSize(backup.size)}</span>
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">{formatDate(backup.createdAt)}</span>
                        <span className={`text-[9px] font-bold px-3 py-1 rounded-full font-oswald uppercase tracking-widest border shadow-sm ${
                          backup.status === 'success' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                          backup.status === 'failed' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                          'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                        }`}>
                          {backup.status === 'success' ? 'Sucesso' :
                           backup.status === 'failed' ? 'Falhou' : 'Em progresso'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleDownloadBackup(backup.fileName)}
                      className="px-4 py-2 text-[10px] font-bold font-oswald text-blue-400 hover:text-white bg-blue-400/5 hover:bg-blue-600 rounded-xl border border-blue-400/20 hover:border-blue-500 transition-all flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      onClick={() => {
                        // handleRestoreBackup(); // Comentado pois handleRestoreBackup foi removido por não estar sendo usado corretamente
                      }}
                      className="px-4 py-2 text-[10px] font-bold font-oswald text-orange-400 hover:text-white bg-orange-400/5 hover:bg-orange-600 rounded-xl border border-orange-400/20 hover:border-orange-500 transition-all flex items-center gap-2"
                    >
                      <RefreshCcw className="w-4 h-4" />
                      Restaurar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Configuração */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-md w-full overflow-hidden relative">
            <div className="p-8 border-b border-white/10 bg-white/5">
              <h3 className="text-2xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
                <Settings className="w-6 h-6 text-blue-400" />
                Configurar Backup
              </h3>
              <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase tracking-[0.2em] font-oswald">Automação de segurança</p>
            </div>

            <div className="p-8 space-y-8">
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-all" onClick={() => setConfig({ ...config, enabled: !config.enabled })}>
                <div className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all ${config.enabled ? 'bg-blue-600 border-blue-500' : 'border-white/20'}`}>
                  {config.enabled && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <label className="text-xs font-bold font-oswald text-white uppercase tracking-widest cursor-pointer">
                  Ativar backup automático
                </label>
              </div>

              {config.enabled && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold font-oswald text-gray-400 uppercase tracking-[0.2em] ml-1">
                      Frequência do Agendamento
                    </label>
                    <select
                      value={config.schedule}
                      onChange={(e) => setConfig({ ...config, schedule: e.target.value })}
                      className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-white font-oswald text-sm appearance-none cursor-pointer"
                    >
                      {SCHEDULE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value} className="bg-[#1e293b] text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold font-oswald text-gray-400 uppercase tracking-[0.2em] ml-1">
                      Retenção (Dias)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={config.retentionDays}
                      onChange={(e) => setConfig({ ...config, retentionDays: parseInt(e.target.value) })}
                      className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-white font-oswald placeholder-gray-600"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-8 bg-white/5 border-t border-white/10 flex gap-4">
              <button
                onClick={() => setShowConfigModal(false)}
                className="flex-1 px-6 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 transition-all font-oswald"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveConfig}
                className="flex-1 px-6 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:bg-blue-500 transition-all font-oswald border border-blue-400/50"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-md w-full overflow-hidden relative">
            <div className="p-8 border-b border-white/10 bg-white/5">
              <h3 className="text-2xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
                <Upload className="w-6 h-6 text-orange-400" />
                Upload de Backup
              </h3>
              <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase tracking-[0.2em] font-oswald">Restauração manual de arquivo</p>
            </div>

            <div className="p-8 space-y-8">
              <div className="relative group/upload">
                <input
                  type="file"
                  accept=".tar.gz"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="backup-upload"
                />
                <label
                  htmlFor="backup-upload"
                  className="flex flex-col items-center justify-center gap-4 p-12 rounded-3xl bg-white/5 border-2 border-dashed border-white/10 hover:border-orange-500/50 hover:bg-orange-500/5 transition-all cursor-pointer group-hover/upload:scale-[0.99]"
                >
                  <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center border border-white/10 group-hover:border-orange-500/20 transition-all">
                    <Upload className="w-8 h-8 text-gray-500 group-hover:text-orange-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-bold text-white uppercase tracking-widest font-oswald">
                      {uploadFile ? uploadFile.name : 'Selecionar arquivo .tar.gz'}
                    </p>
                    <p className="text-[9px] font-bold text-gray-400 mt-2 uppercase tracking-widest font-oswald">
                      Arraste ou clique para selecionar
                    </p>
                  </div>
                </label>
              </div>

              <div className="p-4 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex gap-4">
                <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0" />
                <p className="text-[9px] font-bold text-orange-400/80 uppercase tracking-widest leading-relaxed font-oswald">
                  O arquivo selecionado será usado para substituir todos os dados atuais do sistema. Use com cautela.
                </p>
              </div>
            </div>

            <div className="p-8 bg-white/5 border-t border-white/10 flex gap-4">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                className="flex-1 px-6 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 transition-all font-oswald"
              >
                Cancelar
              </button>
              <button
                onClick={handleUploadAndRestore}
                disabled={!uploadFile || uploading}
                className="flex-1 px-6 py-4 bg-orange-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-orange-900/40 hover:bg-orange-500 transition-all font-oswald border border-orange-400/50 disabled:opacity-50"
              >
                {uploading ? 'Processando...' : 'Fazer Upload e Restaurar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação Generico */}
      <AlertDialog.Root open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, isOpen: open })}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] animate-fadeIn" />
          <AlertDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] p-10 backdrop-blur-3xl shadow-2xl w-full max-w-md z-[70] animate-scaleIn">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className={`w-20 h-20 rounded-3xl flex items-center justify-center border-2 transition-all shadow-lg ${
                confirmDialog.type === 'danger' ? 'bg-red-500/10 border-red-500/20 text-red-500 shadow-red-900/20' : 'bg-blue-500/10 border-blue-500/20 text-blue-500 shadow-blue-900/20'
              }`}>
                {confirmDialog.type === 'danger' ? <AlertTriangle className="w-10 h-10" /> : <ShieldCheck className="w-10 h-10" />}
              </div>
              
              <div className="space-y-3">
                <AlertDialog.Title className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">
                  {confirmDialog.title}
                </AlertDialog.Title>
                <AlertDialog.Description className="text-gray-300 font-oswald uppercase tracking-[0.1em] text-[10px] font-bold leading-relaxed whitespace-pre-line">
                  {confirmDialog.message}
                </AlertDialog.Description>
              </div>

              <div className="flex gap-4 w-full pt-4">
                <AlertDialog.Cancel className="flex-1 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-bold font-oswald text-gray-300 uppercase tracking-[0.2em] transition-all">
                  Cancelar
                </AlertDialog.Cancel>
                <AlertDialog.Action
                  onClick={confirmDialog.onConfirm}
                  className={`flex-1 px-6 py-4 rounded-2xl text-[10px] font-bold font-oswald text-white uppercase tracking-[0.2em] transition-all border shadow-lg ${
                    confirmDialog.type === 'danger' ? 'bg-red-600 hover:bg-red-500 border-red-500 shadow-red-900/20' : 'bg-blue-600 hover:bg-blue-500 border-blue-500 shadow-blue-900/20'
                  }`}
                >
                  Confirmar Ação
                </AlertDialog.Action>
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
